import React from 'react';
import { Box, Typography } from '@mui/material';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import type { IInfluencer } from '../../redux/buzzbridge/types';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../../pages/BrandDashboard/brandDashboardTokens';

const ON_VARIANT = 'rgba(255,255,255,0.55)';

function formatRating(n?: number): string {
    if (n == null || !Number.isFinite(Number(n))) return '—';
    return Number(n).toFixed(1);
}

function reviewerLabel(review: NonNullable<IInfluencer['recent_reviews']>[number]): string {
    const brand = review.brand?.name?.trim();
    if (brand) return brand;
    const u = review.user;
    const name = [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim();
    if (name) return name;
    if (u?.userName) return `@${u.userName}`;
    return 'Brand';
}

export function publicRatingLabel(influencer: IInfluencer): string {
    const count = influencer.number_of_reviews ?? influencer.recent_reviews?.length ?? 0;
    if (!count) return '—';
    return formatRating(influencer.ratings);
}

interface Props {
    influencer: IInfluencer;
    /** Match drawer vs page spacing */
    compact?: boolean;
}

/** Rating & review cards — same surface language as reach / engagement stats. */
export const InfluencerPublicReviewsSection: React.FC<Props> = ({ influencer, compact }) => {
    const reviews = influencer.recent_reviews || [];
    const count = influencer.number_of_reviews ?? reviews.length;
    if (!count && reviews.length === 0) {
        return (
            <Box
                sx={{
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    borderRadius: 2,
                    p: compact ? 2.5 : { xs: 2.5, md: 4 },
                }}>
                <Typography
                    sx={{
                        fontSize: 11,
                        color: ON_VARIANT,
                        textTransform: 'uppercase',
                        letterSpacing: '0.14em',
                        mb: 1,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    }}>
                    Ratings & reviews
                </Typography>
                <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    No reviews yet
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography
                sx={{
                    fontSize: 11,
                    color: ON_VARIANT,
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    mb: 1.5,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                }}>
                Ratings & reviews
                {count > 0 ? ` · ${count}` : ''}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {reviews.map((review, i) => (
                    <Box
                        key={review._id || `${reviewerLabel(review)}-${i}`}
                        sx={{
                            bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                            borderRadius: 2,
                            p: compact ? 2 : { xs: 2, md: 2.5 },
                            transition: 'background-color 0.3s',
                            '&:hover': { bgcolor: BRAND_SURFACE_CONTAINER },
                        }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                mb: 0.75,
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 14,
                                    color: '#fff',
                                }}>
                                {reviewerLabel(review)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                                <StarRoundedIcon sx={{ fontSize: 18, color: BRAND_LUXE_PRIMARY }} />
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 18,
                                        color: BRAND_LUXE_PRIMARY,
                                        lineHeight: 1,
                                    }}>
                                    {formatRating(review.rating)}
                                </Typography>
                            </Box>
                        </Box>
                        {review.review_text?.trim() ? (
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: 'rgba(255,255,255,0.7)',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    lineHeight: 1.45,
                                }}>
                                {review.review_text.trim()}
                            </Typography>
                        ) : null}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
